import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { addSeconds, differenceInSeconds } from 'date-fns';
import { useSessionStorage } from 'usehooks-ts';

import { PopupNames } from 'contexts/PopupContext';

import { isMonetizationPage, isTermsOrPolicyPage } from 'helpers/route';
import { isPaywallMonthPackageEnabledSelector } from 'store/common/selectors';

import { usePopup } from './usePopup';

const STORAGE_CONFIG = {
  key: 'STOP_EXIT_POPUP_COOLDOWN',
  cooldownSeconds: 60,
};

export const useStopExitPopup = () => {
  const { pathname } = useLocation();

  const isStopExistEnable = useSelector(isPaywallMonthPackageEnabledSelector);

  const { handleOpenForce, handleAction, handleClose } = usePopup(
    PopupNames.StopExist
  );

  const [cooldownTime, setCooldownTime] = useSessionStorage<string | null>(
    STORAGE_CONFIG.key,
    null
  );

  const [isUpgradePrevPage, setIsUpgradePrevPage] = useState(false);

  const isCooldownExpired = useCallback(() => {
    if (!cooldownTime) return true;

    return differenceInSeconds(new Date(), new Date(cooldownTime)) > 0;
  }, [cooldownTime]);

  const updateCooldownTime = useCallback(() => {
    const newCooldownTime = addSeconds(
      new Date(),
      STORAGE_CONFIG.cooldownSeconds
    );
    setCooldownTime(String(newCooldownTime));
  }, [setCooldownTime]);

  useEffect(() => {
    const isShouldResetPrevPage =
      isUpgradePrevPage &&
      !isMonetizationPage(pathname) &&
      !isTermsOrPolicyPage(pathname);
    const isShouldShowPopup =
      isStopExistEnable &&
      isCooldownExpired() &&
      !isMonetizationPage(pathname) &&
      !isTermsOrPolicyPage(pathname);

    if (pathname === '/upgrade') {
      setIsUpgradePrevPage(true);
      return;
    }

    if (isShouldResetPrevPage) {
      if (isShouldShowPopup) {
        handleOpenForce(null);
        updateCooldownTime();
      }
      setIsUpgradePrevPage(false);
    }
  }, [
    pathname,
    isUpgradePrevPage,
    isStopExistEnable,
    isCooldownExpired,
    handleOpenForce,
    updateCooldownTime,
  ]);

  useEffect(() => {
    handleAction.current = (action: { type: string }) => {
      if (action.type === 'close') handleClose();
    };
  }, [handleAction, handleClose]);
};
